import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export interface Plan {
  _id: string;
  name: string;
  price: number;
  duration: number;
  durationType: PlanDurationType;
  status: PlanStatus;
  description?: string;
  currency: string;
  gyms: string[]; // Changed from gym: string to gyms: string[]
  daysPerMonth?: number;
  isClass?: boolean;
  classId?: string[];
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
}

export type PlanDurationType = 'days' | 'months' | 'years' | 'fixed';
export type PlanStatus = 'active' | 'inactive';

// Plain function to fetch plans (not a hook)
export const getPlans = async (): Promise<Plan[]> => {
  try {
    const response = await apiClient.get('/plans');
    return response.data as Plan[];
  } catch (error) {
    console.error('Error fetching plans:', error);
    throw error;
  }
};

export const usePlans = (gymId?: string) => {
  return useQuery({
    queryKey: ['plans', { gymId }], // Use an object to make the key more stable
    queryFn: async () => {
      // If gymId is provided, fetch plans for that gym
      const url = gymId ? `/plans` : '/plans';
      console.log(`Fetching plans from: ${url}`);
      try {
        const response = await apiClient.get(url);
        console.log('Plans API response:', response.data);
        return response.data as Plan[];
      } catch (error) {
        console.error('Error fetching plans:', error);
        return [];
      }
    },
    enabled: !!gymId, // Only run the query if gymId is provided
    staleTime: 0, // Changed from 30000 to 0 to always refetch
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    retry: 1, // Only retry once to avoid excessive requests
  });
};

export const usePlan = (id: string) => {
  return useQuery({
    queryKey: ['plans', 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get(`/plans/detail/${id}`);
      return response.data as Plan;
    },
    enabled: !!id,
    staleTime: 30000, // Data is fresh for 30 seconds
    refetchOnWindowFocus: false,
  });
};

export const useCreatePlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (plan: Omit<Plan, '_id'>) => {
      const response = await apiClient.post('/plans', plan);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      // Invalidate for all gyms in the plan
      if (variables.gyms) {
        variables.gyms.forEach((gymId) => {
          queryClient.invalidateQueries({
            queryKey: ['plans', { gymId }],
          });
        });
      }
    },
  });
};

export const useUpdatePlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ _id, ...plan }: Plan) => {
      const response = await apiClient.patch(`/plans/${_id}`, plan);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      // Invalidate for all gyms in the plan
      if (variables.gyms) {
        variables.gyms.forEach((gymId) => {
          queryClient.invalidateQueries({
            queryKey: ['plans', { gymId }],
          });
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['plans', 'detail', variables._id],
      });
    },
  });
};

export const useDeletePlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      console.log(`Deleting plan with ID: ${id}`);
      if (!id) {
        throw new Error('Plan ID is required for deletion');
      }
      const response = await apiClient.delete(`/plans/${id}`);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all plans queries
      queryClient.invalidateQueries({
        queryKey: ['plans'],
      });
    },
    onError: (error) => {
      console.error('Error deleting plan:', error);
    },
  });
};
